import { useEffect, useRef, useState } from "react";
import pageStyle from "../../constants/pageStyle";
import OnlineOrderPDF from "../organism/OnlineOrderPDF";
import { useReactToPrint } from "react-to-print";
import useGetMultipleOrdersPDF from "../../services/utils/custom hooks/OnlineOrder/useGetMultipleOrdersPDF";
import orderResponseFormatter from "../../utils/helpers/orderResponseFormatter";


interface PDFPrintProps {
  ids: string[],
  onStart: () => void,
  onSuccess: () => void,
  onError: (message: string) => void
}

function PDFPrint({ ids, onStart, onError, onSuccess }: Readonly<PDFPrintProps>): JSX.Element {
  const [orders, setOrders] = useState<any>([])
  const [content, setContent] = useState<any>(null)
  // No longer need visible state for display:none
  // const [visible, setVisible] = useState<boolean>(false) 
  const componentRef = useRef<HTMLDivElement>(null)
  const getMultipleOrdersPDF = useGetMultipleOrdersPDF()
  const isProcessingRef = useRef(false)
  const previousIdsRef = useRef<string>("")
  const lastSuccessfulIdsRef = useRef<string>("")

  // Store callbacks in refs to avoid dependency issues
  const onStartRef = useRef(onStart)
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)

  // Update refs when callbacks change
  useEffect(() => {
    onStartRef.current = onStart
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
  }, [onStart, onSuccess, onError])

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: pageStyle,
    onAfterPrint: () => {
      onSuccessRef.current()
      setContent(null)
      // setVisible(false)
      isProcessingRef.current = false
      // Mark these IDs as successfully processed
      lastSuccessfulIdsRef.current = previousIdsRef.current
    },
    copyStyles: false
  })

  useEffect(() => {
    // Only process if ids have changed and we're not already processing
    const idsString = ids.join(",")
    if (ids.length === 0) {
      setContent(null)
      previousIdsRef.current = ""
      lastSuccessfulIdsRef.current = ""
      isProcessingRef.current = false
      return
    }

    // Prevent duplicate calls for the same ids ONLY if they were successfully processed
    // Allow retries if previous call failed
    if (previousIdsRef.current === idsString && lastSuccessfulIdsRef.current === idsString) {
      return
    }

    // Prevent calling if already processing
    if (isProcessingRef.current) {
      return
    }

    previousIdsRef.current = idsString
    isProcessingRef.current = true

    getMultipleOrdersPDF.mutate(
      {
        orderIds: ids,
        onStart: () => {
          onStartRef.current()
        },
        onSuccess: () => {
          // Don't set content here - wait for orders to be set
        },
        onError: (message: string) => {
          isProcessingRef.current = false
          // Reset previousIdsRef on error to allow retry
          previousIdsRef.current = ""
          onErrorRef.current(message)
        },
      },
      {
        onSuccess: (response) => {
          const { orders } = response?.data?.data || {}
          setOrders(orders)
          // Set content after orders are populated
          setContent(true)
        },
        onError: () => {
          isProcessingRef.current = false
          // Reset previousIdsRef on error to allow retry
          previousIdsRef.current = ""
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(",")]) // Only depend on the ids string - getMultipleOrdersPDF is stable

  useEffect(() => {
    if (content && orders.length > 0) {
      // Step 1: Make component visible to allow styles to render
      // setVisible(true)

      const preloadImage = (url: string): Promise<void> => {
        return new Promise((resolve) => {
          if (!url) {
            resolve();
            return;
          }
          const img = new Image();
          img.src = url;
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve on error to avoid blocking
        });
      };

      // Step 2: Wait for styles and fonts to load
      // Then trigger print and immediately hide
      const triggerPrint = async () => {
        // Wait for fonts to be ready (if using web fonts)
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready
        }

        // Extract all image URLs from orders (primary, secondary, PRAME logo when present)
        const imageUrls = new Set<string>();
        orders.forEach((order: any) => {
          const values = orderResponseFormatter(order);
          if (values?.selectedTest?.imageUrl) {
            imageUrls.add(values.selectedTest.imageUrl);
          }
          if (values?.secondaryTest?.imageUrl) {
            imageUrls.add(values.secondaryTest.imageUrl);
          }
          if (values?.selectedTest?.DecisionDxPRAMELogo) {
            imageUrls.add(values.selectedTest.DecisionDxPRAMELogo);
          }
        });

        // Preload all images explicitly
        await Promise.all(Array.from(imageUrls).map(url => preloadImage(url)));

        // Additional small delay to ensure all styles are computed
        await new Promise(resolve => setTimeout(resolve, 500))

        // Trigger print - react-to-print will clone the content at this moment
        handlePrint()
        // Immediately hide the component (before print dialog opens)
        // The cloned content in the print iframe is independent
        // setVisible(false)
      }

      triggerPrint()

      return () => {
        setContent(null)
      }
    }
  }, [content, orders.length, handlePrint])

  return (
    <>
      <style>
        {`
        #printPDFNewFlow {
          position: fixed;
          left: -10000px;
          top: -10000px;
          width: 210mm; /* A4 width */
        }
        @media print {
          #printPDFNewFlow {
            position: static;
            left: auto;
            top: auto;
            display: block;
          }
        }
      `}
      </style>
      <div id="printPDFNewFlow" ref={componentRef}>
        {orders?.map((order: any, index: number) => (
          <OnlineOrderPDF
            key={order.id}
            addPageBreak={index !== 0}
            isVisible
            formId={''}
            values={orderResponseFormatter(order)}
            isApproval
          />
        ))}
      </div>
    </>
  )
}

export default PDFPrint
